import React, { createContext, useCallback, useState } from 'react';
import { useVisualViewport } from '../../hooks/useVisualViewport';
import c from 'classnames';
import ddt from '@wix/da-ddt';

import s from './AbsoluteHeightLayout.scss';

/**
 * Context allowing setting of the scrollable for body locking and also
 * height for inner components
 */
export const AbsoluteHeightLayoutContext = createContext<{
  height: number | false;
}>({
  height: 0,
});
AbsoluteHeightLayoutContext.displayName = 'AbsoluteHeightLayoutContext';

export interface Props {
  /* Remove more height from the viewport calculation, for headers etc */
  heightReduction?: number;

  /* customise the containers styles */
  className?: string;

  /* Allow scale to be set based on VVP  - default false because it does strange things... */
  withScale?: boolean;

  /* Disable if dv units are supported by the browser - default false */
  disableIfDvSupported?: boolean;
}

/**
 * Deals with the times where you want a fixed layout that scrolls but also
 * works on IOS where screen height is never right and 100vh is broken.
 *
 * This applies:
 * - visualViewport height/offset/scale a wrapping div
 *
 * Assumes:
 * - The layout is a fixed height
 *
 * Provides:
 * - AbsoluteHeightLayoutContext for easy access to the height in children
 *
 * Notes:
 * - You probably also want to use body scroll locking on all the scrollable areas in your fixed layout
 * - When using this you "probably" want to disable the fixed positioning of the header, there is a
 *   flag on the site header component for that. This makes working with IOS keyboard much easier.
 *
 * See stories for example
 *
 * @deprecated Use dvh instead, ios finally fixed it so you can use css + scroll locking and it work with keyboard
 */
const AbsoluteHeightLayout: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  className,
  heightReduction,
  withScale = false,
  disableIfDvSupported = false,
}) => {
  const [visualViewport, setVisualViewport] = useState<
    VisualViewport | undefined
  >();
  const viewportHandler = useCallback((v: VisualViewport) => {
    setVisualViewport(v);
  }, []);
  useVisualViewport(viewportHandler, true);

  let styles: React.CSSProperties = {};
  let calculatedHeight: number = 0;
  if (visualViewport) {
    calculatedHeight = heightReduction
      ? visualViewport.height - heightReduction
      : visualViewport.height;
    styles = {
      height: calculatedHeight,
      transform:
        visualViewport.offsetLeft > 0 || visualViewport.offsetTop > 0
          ? `translate(${visualViewport.offsetLeft}px,${visualViewport.offsetTop}px)`
          : undefined,
      scale: withScale ? visualViewport.scale : undefined,
    };
  }

  if (
    disableIfDvSupported &&
    typeof window !== 'undefined' &&
    CSS.supports &&
    CSS.supports('height', '1dvh')
  ) {
    ddt.log('layout', 'Disabled abs height using DVH');
    return (
      <AbsoluteHeightLayoutContext.Provider
        value={{
          height: false,
        }}
      >
        <div className={c(s['root'], className)}>{children}</div>
      </AbsoluteHeightLayoutContext.Provider>
    );
  }

  return (
    <AbsoluteHeightLayoutContext.Provider
      value={{
        height: calculatedHeight,
      }}
    >
      <div className={c(s['root'], className)} style={styles}>
        {children}
      </div>
    </AbsoluteHeightLayoutContext.Provider>
  );
};
AbsoluteHeightLayout.displayName = 'AbsoluteHeightLayout';

export default AbsoluteHeightLayout;
